import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { ArrowLeft, Upload, Image as ImageIcon } from 'lucide-react';
import toast from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminSettings() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({ auto_cleanup_enabled: false });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        await Promise.all([fetchSettings(), fetchLogo()]);
      } finally {
        setPageLoading(false);
      }
    };
    loadData();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      const response = await axios.get(`${API}/admin/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setSettings(response.data || { auto_cleanup_enabled: false });
    } catch (error) {
      console.error('Error loading settings:', error);
      // Set default if error
      setSettings({ auto_cleanup_enabled: false });
    }
  };

  const fetchLogo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/settings/logo`, { 
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const url = URL.createObjectURL(response.data);
      setLogoPreview(url);
    } catch (error) {
      // Logo doesn't exist yet - this is ok
      console.log('No logo found yet');
    }
  };

  const handleToggleCleanup = async (enabled) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      
      // Update local state first for immediate feedback
      setSettings(prev => ({ ...prev, auto_cleanup_enabled: enabled }));
      
      // Then update backend
      await axios.put(`${API}/admin/settings`, 
        { auto_cleanup_enabled: enabled },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      toast.success('Configuración actualizada');
    } catch (error) {
      console.error('Error updating settings:', error);
      // Revert on error
      setSettings(prev => ({ ...prev, auto_cleanup_enabled: !enabled }));
      toast.error(error.response?.data?.detail || 'Error al actualizar configuración');
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile) {
      toast.error('Por favor selecciona un logo');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('file', logoFile);

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/settings/logo`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      toast.success('Logo actualizado');
      fetchLogo();
      setLogoFile(null);
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error(error.response?.data?.detail || 'Error al subir logo');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const url = URL.createObjectURL(file);
      setLogoPreview(url);
    }
  };

  if (pageLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-blue-50/40">
        <div className="text-lg font-medium text-[#2c57c5]">Cargando configuración...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/40">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
        </div>

        <div className="max-w-2xl mx-auto space-y-6">
          <Card className="p-6">
            <h1 className="text-3xl font-bold text-slate-900 mb-6">Configuraciones</h1>

            <div className="space-y-6">
              {/* Auto Cleanup */}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5 flex-1">
                  <Label className="text-base">Limpieza Automática de Datos</Label>
                  <p className="text-sm text-slate-600">
                    Elimina automáticamente archivos y chat de pagos de contado después de 72 horas
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.auto_cleanup_enabled}
                    onChange={(e) => handleToggleCleanup(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                </label>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Logo del Sistema</h2>
            
            <div className="space-y-4">
              {logoPreview && (
                <div className="border rounded-lg p-4 bg-slate-50">
                  <img src={logoPreview} alt="Logo" className="max-h-32 mx-auto" />
                </div>
              )}

              <div className="space-y-2">
                <Label>Subir Nuevo Logo</Label>
                <div className="flex items-center space-x-2">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="flex-1"
                  />
                  <Button
                    onClick={handleLogoUpload}
                    disabled={!logoFile || loading}
                    className="bg-[#2c57c5] hover:bg-[#1e3a8a] text-white"
                  >
                    {loading ? (
                      'Subiendo...'
                    ) : (
                      <>
                        <Upload className="mr-2 h-4 w-4" />
                        Subir
                      </>
                    )}
                  </Button>
                </div>
                <p className="text-xs text-slate-500">
                  Formatos aceptados: JPG, PNG, GIF. Tamaño recomendado: 200x60px
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
